import React, { useState, useEffect, useRef } from "react";
import { IonGrid, IonRow, IonCol, IonItem, IonLabel, IonModal, IonContent } from "@ionic/react";
import { Helmet } from "react-helmet";

const gridSize = 5;
const colors = ["#FF5733", "#33FF57", "#3357FF", "#FFD700", "#FF33A1"];
const tempColor = "#ccc";

// Master list of 3-letter words
const masterWords = [
    // Animals & Nature
    "CAT", "DOG", "BAT", "PIG", "COW", "FOX", "OWL", "ANT", "BEE", "BUG",
    "YAK", "RAT", "HEN", "APE", "EMU", "EEL", "RAM", "DOE", "KIT", "CUB",
    "SUN", "SKY", "MUD", "WEB", "ICE", "OIL", "EGG", "SEA", "BAY", "SKY",

    // Objects & Things
    "CAR", "BUS", "BOX", "CAP", "MAP", "PAN", "JAR", "PEN", "POT", "CUP",
    "BED", "BAG", "ZIP", "TOY", "TIN", "FAN", "VAN", "LID", "NET", "AXE",
    "KEY", "BAT", "BAR", "RUG", "LOG", "BIN", "MAT", "LAP", "PAD", "MOP",

    // Actions (verbs)
    "RUN", "HOP", "JOG", "FLY", "TAG", "TRY", "GET", "HIT", "FIX", "ASK",
    "USE", "BUY", "CUT", "DRY", "PAY", "SAY", "WIN", "LAY", "NAP", "YAW",
    "ROW", "TIP", "NOD", "BUN", "TAP", "RAN", "SEE", "ACT", "JAM", "HUG",

    // People & Family
    "MOM", "DAD", "BOY", "MAN", "KID", "PAL", "GAL", "BUD", "MAW", "SIS",

    // Places & Directions
    "DEN", "HUT", "BAY", "GYM", "ZOO", "INN", "BAR", "PIT", "TOP", "LOW",

    // Descriptive words
    "HOT", "COLD", "RED", "BIG", "FUN", "NEW", "OLD", "WET", "DRY", "BAD",
    "MAD", "SAD", "GLU", "ICY", "LOW", "ODD", "DIM", "RAW", "TAN", "FAT",

    // Misc
    "INK", "OFF", "YES", "NOX", "ZEN", "ART", "JOB", "GAP", "KIT", "ACT",
    "FUN", "KIN", "DOT", "JAB", "WAX", "WEB", "FIG", "MIX", "HEN", "SOW",
    "POP", "TOP", "WON", "BUG", "NUT", "PEG", "CAN", "COT", "JET", "PIN"
];


// Utility to get random set of words
const getRandomWords = (count: number): string[] => {
    const shuffled = [...masterWords].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
};

// Utility to get random letter for empty grid cells
const getRandomLetter = () => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return alphabet[Math.floor(Math.random() * alphabet.length)];
};

// Function to place words adjacently into the grid
const placeWordsAdjacently = (words: string[]) => {
    const grid = Array.from({ length: gridSize }, () => Array(gridSize).fill(""));
    const wordPositions: { [key: string]: { row: number; col: number }[] } = {};

    const directions = [
        { row: 0, col: 1 }, // Horizontal
        { row: 1, col: 0 }, // Vertical
        // { row: 1, col: 1 }, // Diagonal
    ];

    words.forEach((word) => {
        let placed = false;
        while (!placed) {
            const startRow = Math.floor(Math.random() * gridSize);
            const startCol = Math.floor(Math.random() * gridSize);
            const direction = directions[Math.floor(Math.random() * directions.length)];
            const positions: { row: number; col: number }[] = [];
            let canPlace = true;

            for (let i = 0; i < word.length; i++) {
                const newRow = startRow + i * direction.row;
                const newCol = startCol + i * direction.col;

                if (
                    newRow >= gridSize ||
                    newCol >= gridSize ||
                    grid[newRow][newCol] !== ""
                ) {
                    canPlace = false;
                    break;
                } else {
                    positions.push({ row: newRow, col: newCol });
                }
            }

            if (canPlace) {
                positions.forEach((pos, index) => {
                    grid[pos.row][pos.col] = word[index];
                });

                wordPositions[word] = positions;
                placed = true;
            }
        }
    });

    // Fill empty grid spaces
    for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
            if (grid[i][j] === "") {
                grid[i][j] = getRandomLetter();
            }
        }
    }

    return { grid, wordPositions };
};

const WordSearch: React.FC = () => {
    const [grid, setGrid] = useState<string[][]>([]);
    const [words, setWords] = useState<string[]>([]);
    const [selectedCells, setSelectedCells] = useState<{ row: number; col: number }[]>([]);
    const [foundWords, setFoundWords] = useState<string[]>([]);
    const [wordPositions, setWordPositions] = useState<{ [key: string]: { row: number; col: number }[] }>({});
    const [cellColors, setCellColors] = useState<{ [key: string]: string }>({});
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Initialize game
    useEffect(() => {
        fetchQuestions();
        document.title = "Phonetic Word Search – LKG Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const handleCellClick = (row: number, col: number) => {
        const key = `${row}-${col}`;

        // Prevent clicking on already colored (found) cells
        if (cellColors[key] && cellColors[key] !== tempColor) return;

        // Prevent selecting the same cell twice
        if (selectedCells.find((cell) => cell.row === row && cell.col === col)) return;

        const newSelection = [...selectedCells, { row, col }];
        setSelectedCells(newSelection);

        const selectedWord = newSelection.map(({ row, col }) => grid[row][col]).join("");

        // Temporary highlight
        setCellColors((prev) => ({ ...prev, [key]: tempColor }));

        if (newSelection.length === 3) {
            const isHorizontal =
                newSelection.every(cell => cell.row === newSelection[0].row) &&
                newSelection
                    .map(cell => cell.col)
                    .sort((a, b) => a - b)
                    .every((val, idx, arr) => idx === 0 || val === arr[idx - 1] + 1);

            const isVertical =
                newSelection.every(cell => cell.col === newSelection[0].col) &&
                newSelection
                    .map(cell => cell.row)
                    .sort((a, b) => a - b)
                    .every((val, idx, arr) => idx === 0 || val === arr[idx - 1] + 1);

            const selectedWord = newSelection.map(({ row, col }) => grid[row][col]).join("");

            if ((isHorizontal || isVertical) && words.includes(selectedWord) && !foundWords.includes(selectedWord)) {
                const newFoundWords = [...foundWords, selectedWord];
                setFoundWords(newFoundWords);

                const wordColor = colors[words.indexOf(selectedWord) % colors.length];
                const newColors = { ...cellColors };
                newSelection.forEach(({ row, col }) => {
                    newColors[`${row}-${col}`] = wordColor;
                });
                setCellColors(newColors);
            } else {
                // Wrong guess or invalid direction – clear temp colors
                const newColors = { ...cellColors };
                newSelection.forEach(({ row, col }) => {
                    delete newColors[`${row}-${col}`];
                });
                setCellColors(newColors);
            }

            setSelectedCells([]);
        }

    };

    const fetchQuestions = () => {

        const randomWords = getRandomWords(5);
        setWords(randomWords);

        const { grid, wordPositions } = placeWordsAdjacently(randomWords);
        setGrid(grid);
        setWordPositions(wordPositions);
    }

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
            setCellColors({})
        }, 2000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG kids improve phonics and reading skills with the Word Search activity on Mama’s Guidance—featuring fun and interactive phonetic word puzzles that boost letter recognition and vocabulary.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart seasons additionWord" style={{ marginTop: 0 }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Phonetic Word Search</h1>

                                <p>
                                    Make phonics fun with this <strong>Phonetic Word Search</strong> worksheet designed especially for LKG learners! This interactive activity helps children <strong>identify and find simple phonetic (CVC) words</strong> like <b><em>cat, dog, pin, sun, bed</em></b>, and more – reinforcing their reading and letter-blending skills.
                                </p>

                                <h2>🔤 What’s Included?</h2>
                                <p>
                                    The worksheet contains a grid filled with letters. Kids are asked to <strong>find and circle short phonetic words</strong> based on sound cues or a word list. These are mostly <strong>CVC (Consonant-Vowel-Consonant)</strong> words perfect for early readers.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize phonetic (CVC) words through visual scanning</li>
                                    <li>Strengthen letter-sound associations</li>
                                    <li>Improve reading and decoding skills</li>
                                    <li>Boost attention, vocabulary, and spelling</li>
                                </ul>

                                <h2>🧩 How to Use This Worksheet</h2>
                                <ul>
                                    <li>Go through the list of phonetic words with your child.</li>
                                    <li>Help them pronounce each word by sounding it out.</li>
                                    <li>Ask them to scan the letter grid and find the words.</li>
                                    <li>Encourage them to circle each word as they find it.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why It’s Useful</h2>
                                <p>
                                    Word searches help children <strong>practice phonics in a fun, game-like setting</strong>. They promote letter recognition, reading fluency, and problem-solving skills while reinforcing what they’ve learned in class.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Phonetic Word Search</h1>

                            <p>
                                Make phonics fun with this <strong>Phonetic Word Search</strong> worksheet designed especially for LKG learners! This interactive activity helps children <strong>identify and find simple phonetic (CVC) words</strong> like <b><em>cat, dog, pin, sun, bed</em></b>, and more – reinforcing their reading and letter-blending skills.
                            </p>

                            <h2>🔤 What’s Included?</h2>
                            <p>
                                The worksheet contains a grid filled with letters. Kids are asked to <strong>find and circle short phonetic words</strong> based on sound cues or a word list. These are mostly <strong>CVC (Consonant-Vowel-Consonant)</strong> words perfect for early readers.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize phonetic (CVC) words through visual scanning</li>
                                <li>Strengthen letter-sound associations</li>
                                <li>Improve reading and decoding skills</li>
                                <li>Boost attention, vocabulary, and spelling</li>
                            </ul>

                            <h2>🧩 How to Use This Worksheet</h2>
                            <ul>
                                <li>Go through the list of phonetic words with your child.</li>
                                <li>Help them pronounce each word by sounding it out.</li>
                                <li>Ask them to scan the letter grid and find the words.</li>
                                <li>Encourage them to circle each word as they find it.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why It’s Useful</h2>
                            <p>
                                Word searches help children <strong>practice phonics in a fun, game-like setting</strong>. They promote letter recognition, reading fluency, and problem-solving skills while reinforcing what they’ve learned in class.
                            </p>
                        </article>
                        <IonGrid>
                            {grid.map((row, rowIndex) => (
                                <IonRow key={rowIndex}>
                                    {row.map((cell, colIndex) => {
                                        const key = `${rowIndex}-${colIndex}`;
                                        const backgroundColor = cellColors[key] || "white";

                                        return (
                                            <IonCol
                                                key={colIndex}
                                                className="word-cell"
                                                onClick={() => handleCellClick(rowIndex, colIndex)}
                                                style={{
                                                    border: "1px solid black",
                                                    padding: "10px",
                                                    textAlign: "center",
                                                    fontWeight: "bold",
                                                    cursor: "pointer",
                                                    backgroundColor,
                                                    userSelect: "none",
                                                }}
                                            >
                                                {cell}
                                            </IonCol>
                                        );
                                    })}
                                </IonRow>
                            ))}
                        </IonGrid>

                        <h3>Words to Find:</h3>
                        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                            {words.map((word, index) => (
                                <p
                                    key={index}
                                    style={{
                                        textDecoration: foundWords.includes(word) ? "line-through" : "none",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {word}
                                </p>
                            ))}
                        </div>
                        <div className="mt-2 mb-4 text-center" style={{ display: 'flex', gap: '10px', justifyContent: "center" }}>
                            <div className="btn hero-btn" style={{ fontSize: '18px' }} onClick={nextQuestion}>Next Question</div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: '150px' }}
                        className="img-fluid"
                        src={process.env.REACT_APP_LOADING_LINK}
                        alt="Well Done"
                    />
                </div>
            )}

        </>
    );
};

export default WordSearch;
